import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib';

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  description?: string;
  errorMessage?: string;
  filter?: (option: ComboboxOption, inputValue: string) => boolean;
  className?: string;
}

const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  ({ className, options, value, onChange, placeholder = 'Seleccionar...', disabled, error, label, description, errorMessage, filter, ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');

    const filteredOptions = React.useMemo(() => {
      if (!inputValue) return options;
      return options.filter((opt) =>
        filter ? filter(opt, inputValue) : opt.label.toLowerCase().includes(inputValue.toLowerCase())
      );
    }, [options, inputValue, filter]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setOpen(true);
    };

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        {label && (
          <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
        )}
        <div className="relative">
          <input
            type="text"
            value={inputValue || (value ? options.find(o => o.value === value)?.label : '')}
            onChange={(e) => {
              setInputValue(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            disabled={disabled}
            placeholder={placeholder}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-10',
              error && 'border-destructive focus:ring-destructive',
              disabled && 'opacity-50'
            )}
          />
          <button
            type="button"
            onClick={() => setOpen(!open)}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Abrir opciones"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        {open && (
          <div
            className="absolute z-50 w-full mt-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                disabled={option.disabled || disabled}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-sm outline-none transition-colors',
                  value === option.value && 'bg-accent text-accent-foreground',
                  'hover:bg-accent hover:text-accent-foreground',
                  option.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="flex-1">{option.label}</span>
              </button>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Sin opciones disponibles
              </div>
            )}
          </div>
        )}
        {description && !errorMessage && (
          <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
        )}
        {errorMessage && (
          <p className="mt-1.5 text-sm text-destructive" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  }
);
Combobox.displayName = 'Combobox';

export { Combobox };